import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, AlertCircle, Loader2, CloudUpload, MapPin, ArrowUp, ArrowDown, Trash2, Plus, X } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { toast } from 'sonner';

const CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'Pune', 'Hyderabad'];
const CITY_COORDS = {
  Mumbai: { lat: 19.076, lng: 72.8777 }, Delhi: { lat: 28.7041, lng: 77.1025 },
  Bangalore: { lat: 12.9716, lng: 77.5946 }, Pune: { lat: 18.5204, lng: 73.8567 },
  Hyderabad: { lat: 17.385, lng: 78.4867 },
};
const AGE_OPTIONS = ['all', '18+', '21+'];

function resolveImageUrl(u) {
  if (!u) return undefined;
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function OrgEventFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [banner, setBanner] = useState('');
  const [category, setCategory] = useState('Music');
  const [city, setCity] = useState('Mumbai');
  const [venueName, setVenueName] = useState('');
  const [venueAddr, setVenueAddr] = useState('');
  const [venueLat, setVenueLat] = useState(null);
  const [venueLng, setVenueLng] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [age, setAge] = useState('all');
  const [ticketHint, setTicketHint] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [currentStatus, setCurrentStatus] = useState('draft');

  // Venue picker temp state
  const [tempLat, setTempLat] = useState('');
  const [tempLng, setTempLng] = useState('');

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => {});
    if (isEdit && id) {
      (async () => {
        try {
          const r = await api.get(`/organizer/events/${id}`);
          const e = r.data;
          setTitle(e.title); setDesc(e.description); setBanner(e.banner_url);
          setCategory(e.category); setCity(e.city); setVenueName(e.venue_name);
          setVenueAddr(e.venue_address); setVenueLat(e.venue_lat); setVenueLng(e.venue_lng);
          setAge(e.age_restriction || 'all');
          setTicketHint(e.ticket_type_hint || '');
          setStartAt(e.start_datetime ? dayjs(e.start_datetime).format('YYYY-MM-DDTHH:mm') : '');
          setEndAt(e.end_datetime ? dayjs(e.end_datetime).format('YYYY-MM-DDTHH:mm') : '');
          setFaqs((e.faqs || []).map((f) => ({ id: f.id, question: f.question, answer: f.answer, position: f.position })));
          setCurrentStatus(e.status || 'draft');
        } catch (e) { toast.error(apiErrorMessage(e)); navigate(-1); }
        finally { setLoading(false); }
      })();
    }
  }, [isEdit, id, navigate]);

  const canSubmit = useMemo(() => {
    return title.trim() && desc.trim() && banner.trim() && venueName.trim() && venueAddr.trim() && startAt && endAt;
  }, [title, desc, banner, venueName, venueAddr, startAt, endAt]);

  // Banner upload
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/uploads/image', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBanner(res.data.url);
      toast.success('Banner uploaded');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setUploading(false); }
  };

  // FAQ helpers
  const addFaq = () => {
    if (faqs.length >= 10) { toast.info('Maximum 10 FAQs'); return; }
    setFaqs((prev) => [...prev, { question: '', answer: '', position: prev.length }]);
  };
  const updateFaq = (i, patch) => setFaqs((prev) => prev.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const removeFaq = (i) => setFaqs((prev) => prev.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, position: idx })));
  const moveFaq = (i, dir) => {
    setFaqs((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((f, idx) => ({ ...f, position: idx }));
    });
  };

  const buildPayload = () => {
    const coords = venueLat != null && venueLng != null
      ? { lat: venueLat, lng: venueLng }
      : CITY_COORDS[city] || { lat: 0, lng: 0 };
    const startISO = new Date(startAt).toISOString();
    const endISO = new Date(endAt).toISOString();
    return {
      title: title.trim(), description: desc.trim(), banner_url: banner.trim(),
      gallery: [banner.trim()], category, city,
      venue_name: venueName.trim(), venue_address: venueAddr.trim(),
      venue_lat: coords.lat, venue_lng: coords.lng,
      start_datetime: startISO, end_datetime: endISO,
      age_restriction: age, ticket_type_hint: ticketHint.trim() || null,
      faqs: faqs.filter((f) => f.question.trim() && f.answer.trim())
        .map((f, idx) => ({ id: f.id, question: f.question.trim(), answer: f.answer.trim(), position: idx })),
    };
  };

  const persistAndMaybePublish = async (publish) => {
    setErr(null);
    if (!canSubmit) { setErr('Please fill in all required fields'); return; }
    for (const f of faqs) {
      if ((f.question.trim() && !f.answer.trim()) || (!f.question.trim() && f.answer.trim())) {
        setErr('Each FAQ needs both question and answer (or remove empty rows)'); return;
      }
    }
    setSaving(true);
    try {
      let eid = id;
      if (isEdit && eid) {
        await api.patch(`/organizer/events/${eid}`, buildPayload());
      } else {
        const r = await api.post('/organizer/events', buildPayload());
        eid = r.data.id;
      }
      if (publish && eid && currentStatus === 'draft') {
        await api.post(`/organizer/events/${eid}/submit`);
        toast.success("Event submitted for approval — you'll be notified once it's live.");
      } else {
        toast.success(isEdit ? 'Saved' : 'Draft created');
      }
      if (!isEdit && eid) navigate(`/organizer/events/${eid}`, { replace: true }); else navigate(-1);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-magenta" /></div>;

  const hasCoords = venueLat != null && venueLng != null;
  const canPublish = currentStatus === 'draft';
  const bannerPreviewUrl = resolveImageUrl(banner);

  return (
    <div className="max-w-2xl mx-auto px-6 py-4" data-testid="org-event-form-page">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="event-form-back-btn" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <h2 className="text-base font-black text-gray-900">{isEdit ? 'Edit Event' : 'New Event'}</h2>
        <div className="w-6" />
      </div>

      <div className="flex flex-col gap-4">
        {/* Banner */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-gray-500">Banner image</label>
          <input data-testid="event-form-banner-input" value={banner} onChange={(e) => setBanner(e.target.value)}
            placeholder="https://... or upload below" className="input-field" />
          <label className={`flex items-center justify-center gap-2 py-3 rounded-xl border-[1.5px] border-brand-magenta bg-pink-50 cursor-pointer hover:bg-pink-100 transition-colors ${uploading ? 'opacity-60' : ''}`}>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" data-testid="event-form-upload-input" disabled={uploading} />
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-brand-magenta" /> : <CloudUpload className="w-4 h-4 text-brand-magenta" />}
            <span className="text-sm font-extrabold text-brand-magenta">Upload from device</span>
          </label>
          {bannerPreviewUrl && <img src={bannerPreviewUrl} alt="Preview" data-testid="event-form-banner-preview" className="w-full aspect-video object-cover rounded-xl bg-gray-100 mt-1" />}
        </div>

        <Field label="Title">
          <input data-testid="event-form-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" className="input-field" />
        </Field>
        <Field label="Description">
          <textarea data-testid="event-form-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What's this event about?" rows={4} className="input-field resize-none" />
        </Field>

        <div>
          <label className="text-xs font-bold text-gray-500 mb-2 block">Category</label>
          <div className="flex flex-wrap gap-2">
            {(categories.length ? categories : [{ id: 'p', name: 'Music' }]).map((c) => (
              <button key={c.id} type="button" data-testid={`event-form-cat-${c.name}`} onClick={() => setCategory(c.name)}
                className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${category === c.name ? 'bg-brand-magenta border-brand-magenta text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-500 mb-2 block">City</label>
          <div className="flex flex-wrap gap-2">
            {CITIES.map((c) => (
              <button key={c} type="button" data-testid={`event-form-city-${c}`} onClick={() => setCity(c)}
                className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${city === c ? 'bg-brand-magenta border-brand-magenta text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <Field label="Venue name">
          <input data-testid="event-form-venue-input" value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="e.g. NCPA Theatre" className="input-field" />
        </Field>
        <Field label="Venue address">
          <input data-testid="event-form-address-input" value={venueAddr} onChange={(e) => setVenueAddr(e.target.value)} placeholder="Street, area" className="input-field" />
        </Field>

        {/* Map picker */}
        <button type="button" data-testid="event-form-map-picker-btn" onClick={() => { setTempLat(String(venueLat ?? CITY_COORDS[city]?.lat ?? 19.076)); setTempLng(String(venueLng ?? CITY_COORDS[city]?.lng ?? 72.8777)); setPickerOpen(true); }}
          className="flex items-center justify-center gap-2 py-3 rounded-xl border-[1.5px] border-gray-200 bg-white text-gray-900 font-bold text-sm hover:bg-gray-50 transition-colors">
          <MapPin className="w-4 h-4 text-brand-magenta" />
          {hasCoords ? 'Edit map location' : 'Pick on map'}
        </button>
        {hasCoords && (
          <div className="flex items-center gap-2" data-testid="event-form-coord-chip">
            <span className="inline-flex items-center gap-1.5 bg-pink-50 text-brand-magenta text-xs font-bold px-3 py-1.5 rounded-full">
              Lat {venueLat.toFixed(4)} · Lng {venueLng.toFixed(4)}
            </span>
            <button data-testid="event-form-coord-clear-btn" onClick={() => { setVenueLat(null); setVenueLng(null); }} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <Field label="Start date & time">
          <input data-testid="event-form-start-input" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} className="input-field" />
        </Field>
        <Field label="End date & time">
          <input data-testid="event-form-end-input" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} className="input-field" />
        </Field>

        <div>
          <label className="text-xs font-bold text-gray-500 mb-2 block">Age restriction</label>
          <div className="flex gap-2">
            {AGE_OPTIONS.map((a) => (
              <button key={a} type="button" data-testid={`event-form-age-${a}`} onClick={() => setAge(a)}
                className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${age === a ? 'bg-brand-magenta border-brand-magenta text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                {a === 'all' ? 'All ages' : a}
              </button>
            ))}
          </div>
        </div>

        <Field label="Ticket type hint (optional)">
          <input data-testid="event-form-tickethint-input" value={ticketHint} onChange={(e) => setTicketHint(e.target.value)} placeholder="e.g. Free, Paid, RSVP" className="input-field" />
        </Field>

        {/* FAQ section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex flex-col gap-3 mt-2" data-testid="event-form-faq-section">
          <h3 className="text-[15px] font-black text-gray-900">Frequently Asked Questions</h3>
          <p className="text-xs text-gray-500 -mt-1">Add up to 10 helpful Q&As. Customers see these on the event page.</p>
          {faqs.map((f, i) => (
            <div key={i} className="bg-gray-50 rounded-xl border border-gray-100 p-3 flex flex-col gap-2" data-testid={`event-form-faq-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-black text-gray-400 uppercase tracking-wide">FAQ #{i + 1}</span>
                <div className="flex items-center gap-3">
                  <button data-testid={`event-form-faq-up-${i}`} onClick={() => moveFaq(i, -1)} disabled={i === 0} className="disabled:opacity-30"><ArrowUp className="w-4 h-4 text-gray-500" /></button>
                  <button data-testid={`event-form-faq-down-${i}`} onClick={() => moveFaq(i, 1)} disabled={i === faqs.length - 1} className="disabled:opacity-30"><ArrowDown className="w-4 h-4 text-gray-500" /></button>
                  <button data-testid={`event-form-faq-remove-${i}`} onClick={() => removeFaq(i)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </div>
              </div>
              <input data-testid={`event-form-faq-question-${i}`} value={f.question} onChange={(e) => updateFaq(i, { question: e.target.value })} placeholder="Question" maxLength={200} className="input-field" />
              <textarea data-testid={`event-form-faq-answer-${i}`} value={f.answer} onChange={(e) => updateFaq(i, { answer: e.target.value })} placeholder="Answer" maxLength={1000} rows={2} className="input-field resize-none" />
            </div>
          ))}
          <button data-testid="event-form-faq-add-btn" onClick={addFaq} type="button"
            className="flex items-center justify-center gap-2 py-3 rounded-xl border-[1.5px] border-dashed border-brand-magenta text-brand-magenta font-extrabold text-sm hover:bg-pink-50 transition-colors">
            <Plus className="w-4 h-4" /> Add FAQ ({faqs.length}/10)
          </button>
        </div>

        {err && (
          <div data-testid="event-form-error" className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-semibold">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {err}
          </div>
        )}

        {/* CTAs */}
        <button data-testid="event-form-save-draft-btn" type="button" disabled={saving}
          onClick={() => persistAndMaybePublish(false)}
          className="w-full border-[1.5px] border-gray-200 bg-white py-4 rounded-2xl font-extrabold text-[15px] text-gray-900 hover:bg-gray-50 disabled:opacity-60 transition-colors mt-2">
          {isEdit ? 'Save Changes' : 'Save as Draft'}
        </button>

        {canPublish && (
          <button data-testid="event-form-publish-btn" type="button" disabled={saving}
            onClick={() => persistAndMaybePublish(true)}
            className="w-full brand-gradient disabled:opacity-60 text-white py-4 rounded-2xl font-black text-base transition-opacity">
            {saving ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Publish Event'}
          </button>
        )}
        <p className="text-center text-[11px] text-gray-400 mb-4">Publishing sends the event to admin for approval. You'll be notified when it goes live.</p>
      </div>

      {/* Venue picker modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPickerOpen(false)} />
          <div className="relative bg-white rounded-3xl p-6 w-full max-w-md flex flex-col gap-3" data-testid="venue-picker-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-gray-900">Set venue coordinates</h3>
              <button onClick={() => setPickerOpen(false)} data-testid="venue-picker-close-btn"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <p className="text-xs text-gray-500">Enter latitude and longitude for precise map placement.</p>
            <label className="text-xs font-bold text-gray-500 mt-1">Latitude</label>
            <input data-testid="venue-picker-lat-input" value={tempLat} onChange={(e) => setTempLat(e.target.value)} placeholder="19.076" className="input-field" />
            <label className="text-xs font-bold text-gray-500">Longitude</label>
            <input data-testid="venue-picker-lng-input" value={tempLng} onChange={(e) => setTempLng(e.target.value)} placeholder="72.8777" className="input-field" />
            <button data-testid="venue-picker-confirm-btn"
              onClick={() => {
                const la = parseFloat(tempLat); const ln = parseFloat(tempLng);
                if (isNaN(la) || isNaN(ln)) { toast.error('Enter valid numbers'); return; }
                setVenueLat(la); setVenueLng(ln); setPickerOpen(false);
              }}
              className="w-full bg-brand-magenta hover:bg-pink-700 text-white py-3.5 rounded-2xl font-black text-[15px] transition-colors mt-2">
              Use these coordinates
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold text-gray-500">{label}</label>
      {children}
    </div>
  );
}
